//! Collapse/expand controls for the row axis of a pivot report.
//!
//! Core tells us which output rows head a collapsible group
//! (`JsPivotLayout`); this places a disclosure triangle at the right edge of
//! the row-label cell, clear of the label text.

import type { Table } from '@/app/gridGL/cells/tables/Table';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import type { Point } from 'pixi.js';
import { Container, Graphics, Rectangle } from 'pixi.js';

// Half-width of the triangle. Kept small so it fits inside a cell without
// crowding the label.
const TRIANGLE_SIZE = 3.5;

// Gap between the triangle and the right edge of the cell.
const TRIANGLE_MARGIN = 4;

// Grown around the triangle so the control is comfortable to hit.
const HIT_PADDING = 4;

interface Control {
  /** Sheet row of the line this control collapses. */
  row: number;
  /** World coordinates, matching how the other table hit regions are tested. */
  bounds: Rectangle;
}

export class TablePivotOutlineControls extends Container {
  private table: Table;
  private graphics: Graphics;
  private controls: Control[] = [];

  // Optimistic collapse state per sheet row, shown from the moment the control
  // is clicked until the recomputed pivot arrives from core (which clears it
  // via Table.updateCodeCell). Keeps the triangle from lagging the click.
  private pendingToggles: Map<number, boolean> = new Map();

  tableCursor: string | undefined;

  constructor(table: Table) {
    super();
    this.table = table;
    this.graphics = this.addChild(new Graphics());
  }

  update = () => {
    this.graphics.clear();
    this.controls = [];

    const pivot = this.table.codeCell.pivot;
    if (!pivot || this.table.codeCell.spill_error) {
      return;
    }

    const color = getCSSVariableTint('muted-foreground');
    for (const row of pivot.rows) {
      const y = Number(row.y);
      const cell = this.table.sheet.getCellOffsets(this.table.codeCell.x, y);
      const controlX = cell.x + cell.width - TRIANGLE_MARGIN - TRIANGLE_SIZE;
      const controlY = cell.y + cell.height / 2;

      this.drawTriangle(controlX, controlY, this.pendingToggles.get(y) ?? row.collapsed, color);
      this.controls.push({
        row: y,
        bounds: new Rectangle(
          controlX - TRIANGLE_SIZE - HIT_PADDING,
          controlY - TRIANGLE_SIZE - HIT_PADDING,
          (TRIANGLE_SIZE + HIT_PADDING) * 2,
          (TRIANGLE_SIZE + HIT_PADDING) * 2
        ),
      });
    }
  };

  /// Points right when collapsed and down when expanded, the way a disclosure
  /// triangle does everywhere else.
  private drawTriangle = (x: number, y: number, collapsed: boolean, color: number) => {
    const local = { x: x - this.table.tableBounds.x, y: y - this.table.tableBounds.y };
    this.graphics.beginFill(color);
    if (collapsed) {
      this.graphics.drawPolygon([
        local.x - TRIANGLE_SIZE,
        local.y - TRIANGLE_SIZE,
        local.x + TRIANGLE_SIZE,
        local.y,
        local.x - TRIANGLE_SIZE,
        local.y + TRIANGLE_SIZE,
      ]);
    } else {
      this.graphics.drawPolygon([
        local.x - TRIANGLE_SIZE,
        local.y - TRIANGLE_SIZE,
        local.x + TRIANGLE_SIZE,
        local.y - TRIANGLE_SIZE,
        local.x,
        local.y + TRIANGLE_SIZE,
      ]);
    }
    this.graphics.endFill();
  };

  /// Flips the drawn state of the control at `row` immediately, ahead of the
  /// recompute that will deliver the real state.
  optimisticToggle = (row: number) => {
    const line = this.table.codeCell.pivot?.rows.find((r) => Number(r.y) === row);
    if (!line) return;
    this.pendingToggles.set(row, !(this.pendingToggles.get(row) ?? line.collapsed));
    this.update();
    pixiApp.setViewportDirty();
  };

  /// Drops optimistic state; called when a recomputed pivot arrives.
  clearPendingToggles = () => {
    this.pendingToggles.clear();
  };

  pointerMove = (world: Point): boolean => {
    const over = this.controls.some((control) => intersects.rectanglePoint(control.bounds, world));
    this.tableCursor = over ? 'pointer' : undefined;
    return over;
  };

  pointerDown = (world: Point): TablePointerDownResult | undefined => {
    const control = this.controls.find((control) => intersects.rectanglePoint(control.bounds, world));
    if (!control) return;
    return { table: this.table.codeCell, type: 'pivot-outline', row: control.row };
  };
}
